"use client";

import { DefaultChatTransport, generateId } from "ai";
import { useChat } from "@ai-sdk-tools/store";
import type { UIMessage } from "@ai-sdk/react";
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Image from "next/image";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { ChatHeader, ChatInput, type ChatInputMessage, ChatMessages, ChatStatusIndicators, EmptyState, SuggestedPrompts } from "@/components/chat";
import { useChatInterface } from "@/hooks/use-chat-interface";
import { useChatStatus } from "@/hooks/use-chat-status";
import { collectPendingSentenceSegments } from "@/lib/sentence-segmentation";
import { cn } from "@/lib/utils";
// Avatar removed from view - keeping files for future use
// import { AvatarContainer } from "@/components/avatar/avatar-container";

type TtsSegmentEvent = {
  messageId: string;
  segmentId: string;
  sequence: number;
  text: string;
  startIndex: number;
  endIndex: number;
};

type TtsQueueItem = TtsSegmentEvent & {
  audioPromise: Promise<Blob>;
};

function toAbortSafeAudioPromise(promise: Promise<Blob>): Promise<Blob> {
  return promise.catch((error) => {
    const isAbort =
      (error instanceof DOMException && error.name === "AbortError") ||
      (error instanceof Error &&
        (error.name === "AbortError" ||
          /operation was aborted|aborted/i.test(error.message)));

    if (isAbort) {
      return new Blob();
    }

    throw error;
  });
}

function buildRevealIndices(segmentText: string, startIndex: number): number[] {
  const indices: number[] = [];
  const wordPattern = /\S+\s*/g;

  for (const match of segmentText.matchAll(wordPattern)) {
    indices.push(startIndex + match.index! + match[0].length);
  }

  const segmentEndIndex = startIndex + segmentText.length;
  if (indices.length === 0 || indices[indices.length - 1] !== segmentEndIndex) {
    indices.push(segmentEndIndex);
  }

  return indices;
}

export function ChatInterface() {
  const { chatId: routeChatId } = useChatInterface();
  const chatId = useMemo(() => routeChatId ?? generateId(), [routeChatId]);

  const [text, setText] = useState<string>("");
  const [ttsAlignmentEnabled, setTtsAlignmentEnabled] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingServerTtsSegmentsRef = useRef<TtsSegmentEvent[]>([]);
  const [serverTtsEventVersion, setServerTtsEventVersion] = useState(0);

  const { messages, sendMessage, status, stop, setMessages } = useChat({
    id: chatId,
    transport: new DefaultChatTransport({
      api: "/api/chat",
      prepareSendMessagesRequest({ messages, id }) {
        const lastMessage = messages[messages.length - 1] as ChatInputMessage;

        const toolChoice = lastMessage.metadata?.toolChoice;

        return {
          body: {
            message: lastMessage,
            id,
            toolChoice,
            ttsAlignmentEnabled,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        };
      },
    }),
    onData: (part) => {
      const dataPart = part as {
        type?: string;
        data?: unknown;
      };

      if (dataPart.type !== "data-tts-segment" || !dataPart.data) {
        return;
      }

      const segment = dataPart.data as Partial<TtsSegmentEvent>;
      if (
        typeof segment.messageId !== "string" ||
        typeof segment.segmentId !== "string" ||
        typeof segment.sequence !== "number" ||
        typeof segment.text !== "string" ||
        typeof segment.startIndex !== "number" ||
        typeof segment.endIndex !== "number"
      ) {
        return;
      }

      pendingServerTtsSegmentsRef.current.push(segment as TtsSegmentEvent);
      setServerTtsEventVersion((prev) => prev + 1);
    },
  });

  const safeMessages = Array.isArray(messages) ? messages : [];
  const { agentStatus, currentToolCall } = useChatStatus(safeMessages, status);

  const hasMessages = safeMessages.length > 0;

  // Client-side hydrate: whenever route chat changes, fetch and seed messages
  useEffect(() => {
    const seed = async () => {
      if (!routeChatId) return;
      try {
        const res = await fetch(`/api/chats/${routeChatId}/messages`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data);
        }
      } catch {
        // ignore
      }
    };
    seed();
  }, [routeChatId, setMessages]);

  // Extract current assistant message data (including streaming)
  const currentAssistantMessage = useMemo(() => {
    if (!Array.isArray(safeMessages) || safeMessages.length === 0) {
      return {
        id: null as string | null,
        text: "",
      };
    }
    
    const lastMessage = safeMessages[safeMessages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") {
      return {
        id: null as string | null,
        text: "",
      };
    }

    // Defensive check: ensure parts is an array
    if (!lastMessage.parts || !Array.isArray(lastMessage.parts)) {
      return {
        id: lastMessage.id,
        text: "",
      };
    }

    const textParts = lastMessage.parts.filter((part) => part.type === "text");
    const textContent = textParts
      .map((part) => (part.type === "text" ? part.text : ""))
      .join("");
    return {
      id: lastMessage.id,
      text: textContent || "",
    };
  }, [safeMessages]);
  const currentAssistantText = currentAssistantMessage.text;
  const currentAssistantMessageId = currentAssistantMessage.id;

  // TTS state management - sync text display with sentence audio playback
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeMessageIdRef = useRef<string | null>(null);
  const revealedCharacterCountRef = useRef<number>(0);
  const latestAssistantTextRef = useRef<string>("");
  const ttsQueueRef = useRef<TtsQueueItem[]>([]);
  const isPlayingQueueRef = useRef<boolean>(false);
  const pendingAbortControllersRef = useRef<Set<AbortController>>(new Set());
  const localFallbackQueuedForMessageRef = useRef<string | null>(null);
  const localQueuedCharacterCountRef = useRef<number>(0);
  const localSequenceRef = useRef<number>(0);
  const serverTtsActiveMessageIdRef = useRef<string | null>(null);
  const [textAnimationProgress, setTextAnimationProgress] = useState<{
    messageId: string;
    currentIndex: number;
    fullText: string;
  } | null>(null);
  const [ttsPendingMessageIds, setTtsPendingMessageIds] = useState<Set<string>>(new Set());
  const textAnimationTimersRef = useRef<number[]>([]);

  const clearTextAnimationTimers = useCallback(() => {
    if (textAnimationTimersRef.current.length === 0) {
      return;
    }

    textAnimationTimersRef.current.forEach(clearTimeout);
    textAnimationTimersRef.current = [];
  }, []);

  const resetTtsPipeline = useCallback(
    (clearPending = true) => {
      pendingAbortControllersRef.current.forEach((controller) => {
        try {
          controller.abort();
        } catch {
          // Ignore abort races during teardown/reset.
        }
      });
      pendingAbortControllersRef.current.clear();

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      clearTextAnimationTimers();
      ttsQueueRef.current = [];
      isPlayingQueueRef.current = false;
      activeMessageIdRef.current = null;
      revealedCharacterCountRef.current = 0;
      latestAssistantTextRef.current = "";
      pendingServerTtsSegmentsRef.current = [];
      localFallbackQueuedForMessageRef.current = null;
      localQueuedCharacterCountRef.current = 0;
      localSequenceRef.current = 0;
      serverTtsActiveMessageIdRef.current = null;
      setTextAnimationProgress(null);

      if (clearPending) {
        setTtsPendingMessageIds(new Set());
      }
    },
    [clearTextAnimationTimers],
  );

  const fetchQueuedAudioSegment = useCallback(async (segmentId: string) => {
    const controller = new AbortController();
    pendingAbortControllersRef.current.add(controller);

    try {
      const response = await fetch(`/api/tts/segments/${segmentId}`, {
        method: "GET",
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`TTS segment API error: ${response.status}`);
      }

      return await response.blob();
    } finally {
      pendingAbortControllersRef.current.delete(controller);
    }
  }, []);

  const fetchLocalSentenceAudio = useCallback(async (sentenceText: string) => {
    const controller = new AbortController();
    pendingAbortControllersRef.current.add(controller);

    try {
      const response = await fetch("/api/tts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: sentenceText,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`TTS API error: ${response.status}`);
      }

      return await response.blob();
    } finally {
      pendingAbortControllersRef.current.delete(controller);
    }
  }, []);

  const scheduleSentenceReveal = useCallback(
    (item: TtsQueueItem, durationMs: number) => {
      clearTextAnimationTimers();

      const fullText = latestAssistantTextRef.current;
      const revealIndices = buildRevealIndices(item.text, item.startIndex);
      const segmentLength = Math.max(item.endIndex - item.startIndex, 1);

      setTextAnimationProgress({
        messageId: item.messageId,
        currentIndex: Math.max(item.startIndex, revealedCharacterCountRef.current),
        fullText,
      });

      revealIndices.forEach((revealIndex) => {
        const progress = (revealIndex - item.startIndex) / segmentLength;
        const timer = window.setTimeout(() => {
          revealedCharacterCountRef.current = Math.max(
            revealedCharacterCountRef.current,
            revealIndex,
          );
          setTextAnimationProgress((prev) => {
            if (!prev || prev.messageId !== item.messageId) {
              return prev;
            }

            return {
              ...prev,
              currentIndex: revealIndex,
              fullText: latestAssistantTextRef.current,
            };
          });
        }, Math.max(0, Math.round(durationMs * progress)));

        textAnimationTimersRef.current.push(timer);
      });
    },
    [clearTextAnimationTimers],
  );

  const drainTtsQueue = useCallback(async () => {
    if (isPlayingQueueRef.current) {
      return;
    }

    isPlayingQueueRef.current = true;

    try {
      while (ttsQueueRef.current.length > 0) {
        const item = ttsQueueRef.current[0];
        let audioUrl: string | null = null;

        try {
          const audioBlob = await item.audioPromise;
          if (audioBlob.size === 0) {
            continue;
          }

          if (activeMessageIdRef.current !== item.messageId || !ttsAlignmentEnabled) {
            continue;
          }

          audioUrl = URL.createObjectURL(audioBlob);
          const audioElement = new Audio(audioUrl);
          audioElement.preload = "auto";

          if (audioElement.readyState < 1) {
            await new Promise<void>((resolve, reject) => {
              const handleReady = () => {
                audioElement.removeEventListener("loadedmetadata", handleReady);
                audioElement.removeEventListener("error", handleError);
                resolve();
              };
              const handleError = () => {
                audioElement.removeEventListener("loadedmetadata", handleReady);
                audioElement.removeEventListener("error", handleError);
                reject(new Error("Failed to load audio metadata"));
              };

              audioElement.addEventListener("loadedmetadata", handleReady, { once: true });
              audioElement.addEventListener("error", handleError, { once: true });
            });
          }

          if (activeMessageIdRef.current !== item.messageId || !ttsAlignmentEnabled) {
            continue;
          }

          const fallbackDurationMs = Math.max(
            500,
            item.text.trim().split(/\s+/).filter(Boolean).length * 240,
          );
          const durationMs =
            Number.isFinite(audioElement.duration) && audioElement.duration > 0
              ? audioElement.duration * 1000
              : fallbackDurationMs;

          scheduleSentenceReveal(item, durationMs);
          setTtsPendingMessageIds((prev) => {
            if (!prev.has(item.messageId)) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(item.messageId);
            return next;
          });

          audioRef.current = audioElement;

          const playbackFinished = new Promise<void>((resolve, reject) => {
            audioElement.addEventListener("ended", () => resolve(), { once: true });
            audioElement.addEventListener(
              "error",
              () => reject(new Error("Audio playback failed")),
              { once: true },
            );
          });

          await audioElement.play();
          await playbackFinished;

          revealedCharacterCountRef.current = Math.max(
            revealedCharacterCountRef.current,
            item.endIndex,
          );
          setTextAnimationProgress((prev) => {
            if (!prev || prev.messageId !== item.messageId) {
              return prev;
            }

            return {
              ...prev,
              currentIndex: item.endIndex,
              fullText: latestAssistantTextRef.current,
            };
          });
        } catch (error) {
          const isAbort =
            (error instanceof DOMException && error.name === "AbortError") ||
            (error instanceof Error &&
              (error.name === "AbortError" ||
                /operation was aborted|aborted/i.test(error.message)));

          if (!isAbort) {
            console.error("Error generating speech:", error);
          }

          setTtsPendingMessageIds((prev) => {
            if (!prev.has(item.messageId)) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(item.messageId);
            return next;
          });
          setTextAnimationProgress((prev) =>
            prev?.messageId === item.messageId ? null : prev,
          );

          if (isAbort) {
            break;
          }
        } finally {
          clearTextAnimationTimers();
          ttsQueueRef.current.shift();

          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
          }

          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
          }
        }
      }
    } finally {
      isPlayingQueueRef.current = false;
    }
  }, [clearTextAnimationTimers, scheduleSentenceReveal, ttsAlignmentEnabled]);

  useEffect(() => {
    latestAssistantTextRef.current = currentAssistantText;

    setTextAnimationProgress((prev) => {
      if (!prev || prev.messageId !== currentAssistantMessageId) {
        return prev;
      }

      return {
        ...prev,
        fullText: currentAssistantText,
        currentIndex: Math.min(prev.currentIndex, currentAssistantText.length),
      };
    });
  }, [currentAssistantMessageId, currentAssistantText]);

  useEffect(() => {
    if (!ttsAlignmentEnabled) {
      resetTtsPipeline();
      return;
    }

    const lastMessage = safeMessages[safeMessages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") {
      setTtsPendingMessageIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const messageId = lastMessage.id;
    const isStreaming = status === "streaming" || status === "submitted";

    if (activeMessageIdRef.current !== messageId) {
      resetTtsPipeline(false);
      activeMessageIdRef.current = messageId;
      latestAssistantTextRef.current = currentAssistantText;
      localQueuedCharacterCountRef.current = 0;
      localSequenceRef.current = 0;
      serverTtsActiveMessageIdRef.current = null;
      setTextAnimationProgress({
        messageId,
        currentIndex: 0,
        fullText: currentAssistantText,
      });
    }

    if (
      revealedCharacterCountRef.current === 0 &&
      (isStreaming || currentAssistantText.trim().length > 0)
    ) {
      setTtsPendingMessageIds((prev) => {
        if (prev.size === 1 && prev.has(messageId)) {
          return prev;
        }
        return new Set([messageId]);
      });
    }

    if (
      serverTtsActiveMessageIdRef.current !== messageId &&
      !pendingServerTtsSegmentsRef.current.some(
        (segment) => segment.messageId === messageId,
      )
    ) {
      const localSegments = collectPendingSentenceSegments(
        currentAssistantText,
        localQueuedCharacterCountRef.current,
        !isStreaming,
      );

      if (localSegments.length > 0) {
        ttsQueueRef.current.push(
          ...localSegments.map((segment) => ({
            messageId,
            segmentId: `local-${messageId}-${segment.startIndex}`,
            sequence: 1_000_000 + localSequenceRef.current++,
            text: segment.text,
            startIndex: segment.startIndex,
            endIndex: segment.endIndex,
            audioPromise: toAbortSafeAudioPromise(
              fetchLocalSentenceAudio(segment.text),
            ),
          })),
        );
        localQueuedCharacterCountRef.current =
          localSegments[localSegments.length - 1]!.endIndex;
        localFallbackQueuedForMessageRef.current = messageId;
        void drainTtsQueue();
      }
    }

    if (
      !isStreaming &&
      currentAssistantText.trim().length > 0 &&
      revealedCharacterCountRef.current === 0 &&
      ttsQueueRef.current.length === 0 &&
      !pendingServerTtsSegmentsRef.current.some(
        (segment) => segment.messageId === messageId,
      ) &&
      localQueuedCharacterCountRef.current === 0
    ) {
      setTtsPendingMessageIds((prev) => {
        if (!prev.has(messageId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
      setTextAnimationProgress(null);
    }
  }, [
    currentAssistantText,
    drainTtsQueue,
    fetchLocalSentenceAudio,
    resetTtsPipeline,
    safeMessages,
    status,
    ttsAlignmentEnabled,
  ]);

  useEffect(() => {
    if (!ttsAlignmentEnabled) {
      return;
    }

    const activeMessageId = activeMessageIdRef.current;
    if (!activeMessageId || pendingServerTtsSegmentsRef.current.length === 0) {
      return;
    }

    const matchingSegments = pendingServerTtsSegmentsRef.current
      .filter((segment) => segment.messageId === activeMessageId)
      .sort((a, b) => a.sequence - b.sequence);

    if (matchingSegments.length === 0) {
      return;
    }

    serverTtsActiveMessageIdRef.current = activeMessageId;

    pendingServerTtsSegmentsRef.current = pendingServerTtsSegmentsRef.current.filter(
      (segment) => segment.messageId !== activeMessageId,
    );

    const existingKeys = new Set(
      ttsQueueRef.current.map((item) => `${item.messageId}:${item.sequence}`),
    );

    for (const segment of matchingSegments) {
      const key = `${segment.messageId}:${segment.sequence}`;
      if (existingKeys.has(key)) {
        continue;
      }

      existingKeys.add(key);
      ttsQueueRef.current.push({
        ...segment,
        audioPromise: toAbortSafeAudioPromise(
          fetchQueuedAudioSegment(segment.segmentId),
        ),
      });
    }

    ttsQueueRef.current.sort((a, b) => a.sequence - b.sequence);
    void drainTtsQueue();
  }, [drainTtsQueue, fetchQueuedAudioSegment, serverTtsEventVersion, ttsAlignmentEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetTtsPipeline();
    };
  }, [resetTtsPipeline]);

  // Artifact state notifications disabled (avoids client crash when artifacts store is unavailable)

  const handleSubmit = (message: ChatInputMessage) => {
    // If currently streaming or submitted, stop instead of submitting
    if (status === "streaming" || status === "submitted") {
      stop();
      return;
    }

    const hasText = Boolean(message.text);
    const hasAttachments = Boolean(message.files?.length);

    if (!(hasText || hasAttachments)) {
      return;
    }

    resetTtsPipeline();


    if (message.files?.length) {
      toast.success("Files attached", {
        description: `${message.files.length} file(s) attached to message`,
      });
    }

    const submittedText =
      typeof message.text === "string" ? message.text.trim() : "";
    const shouldAddLessonAck =
      message.metadata?.toolChoice === "generateLesson" ||
      /(^|\s)(lesson|curriculum|unit\s+\d|grade|k-?2|k-?5|3-5|6-8|9-12|middle\s+school|high\s+school|seventh|7th|eighth|8th|sixth|6th)\b/i.test(
        submittedText,
      );

    if (shouldAddLessonAck) {
      const snippet =
        submittedText.length > 0
          ? submittedText.replace(/\s+/g, " ").slice(0, 90)
          : "your request";
      const lessonAckMessage: UIMessage = {
        id: generateId(),
        role: "assistant",
        parts: [
          {
            type: "text",
            text: `Perfect — I’m generating a MindUP lesson for "${snippet}". I’ll put the full plan in the artifact panel.`,
          },
        ],
      };
      setMessages((prev) => [...prev, lessonAckMessage]);
    }

    sendMessage({
      text: message.text || "Sent with attachments",
      files: message.files,
      metadata: {
        toolChoice: message.metadata?.toolChoice,
      },
    });
    setText("");
    
    // Dispatch event to notify sidebar to refresh chat history
    // Use a small delay to allow the backend to process the message first
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("chatUpdated"));
      }
    }, 1000);
  };

  const chatInput = (
    <ChatInput
      text={text}
      setText={setText}
      textareaRef={textareaRef as RefObject<HTMLTextAreaElement | null>}
      ttsAlignmentEnabled={ttsAlignmentEnabled}
      setTtsAlignmentEnabled={setTtsAlignmentEnabled}
      onSubmit={handleSubmit}
      status={status}
      hasMessages={hasMessages}
    />
  );

  // Avatar removed from view - keeping files for future use
  // const avatarModelPath =
  //   (typeof window !== "undefined" &&
  //     (window as Window & { __AVATAR_MODEL_PATH__?: string })
  //       .__AVATAR_MODEL_PATH__) ||
  //   process.env.NEXT_PUBLIC_AVATAR_MODEL_PATH ||
  //   "/models/mindup-avatar.glb";

  return (
    <div className="flex flex-col w-full h-full bg-gradient-to-b from-white via-[var(--mindup-off-white)] to-white dark:from-[var(--mindup-soft-black)] dark:via-[#333] dark:to-[var(--mindup-soft-black)] text-[var(--mindup-soft-black)] dark:text-white relative">
      {/* Brand animated pattern background — subtle overlay */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
        aria-hidden="true"
      >
        <source src="/brand/patterns/green-slow-breath.mp4" type="video/mp4" />
      </video>

      {/* Brain image — faded background bottom right */}
      <div className="absolute bottom-0 right-0 w-[min(75vw,840px)] h-[min(75vw,840px)] pointer-events-none opacity-[0.18] dark:opacity-[0.12]">
        <Image
          src="/BRAIN_recolored.png"
          alt=""
          fill
          className="object-contain object-right-bottom"
          aria-hidden
        />
      </div>

      {/* Avatar removed from view - keeping files for future use */}
      {/* <AvatarContainer
        modelPath={avatarModelPath}
        text={latestAssistantMessage}
      /> */}

      {/* Artifact canvas is now in AppShell */}

      {hasMessages ? (
        <>
          {/* Header - fixed at top */}
          <div className="shrink-0 relative z-10 bg-gradient-to-b from-white via-white/98 to-white/80 dark:from-[var(--mindup-soft-black)] dark:via-[var(--mindup-soft-black)]/98 dark:to-[var(--mindup-soft-black)]/80 backdrop-blur-xl px-3 py-4 sm:p-4 sm:pt-5 sm:pb-6 border-b border-gray-100/50 dark:border-white/5">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
              <ChatHeader />
            </div>
          </div>
          
          {/* Messages area - scrollable with flex-1 and min-h-0 to prevent growth */}
          <div className="flex-1 min-h-0 overflow-hidden relative">
            <Conversation className="h-full w-full">
              <ConversationContent className="pb-5 pt-4 sm:pb-6 sm:pt-6">
                <div className="max-w-4xl mx-auto w-full px-3 sm:px-4 lg:px-6">
                  <ChatMessages
                    messages={safeMessages}
                    isStreaming={
                      status === "streaming" || status === "submitted"
                    }
                    showAssistantSkeletonWhileWaiting={ttsAlignmentEnabled}
                    textAnimationState={textAnimationProgress || undefined}
                    ttsPendingMessageIds={ttsPendingMessageIds}
                  />
                  <ChatStatusIndicators
                    agentStatus={agentStatus}
                    currentToolCall={currentToolCall}
                    status={status}
                  />
                </div>
              </ConversationContent>
              <ConversationScrollButton className={cn("bottom-28 sm:bottom-32")} />
            </Conversation>
          </div>

          {/* Input at bottom - fixed at bottom */}
          <div className="shrink-0 relative z-10 bg-gradient-to-t from-white via-white/98 to-white/80 dark:from-[var(--mindup-soft-black)] dark:via-[var(--mindup-soft-black)]/98 dark:to-[var(--mindup-soft-black)]/80 backdrop-blur-xl pt-4 pb-4 px-3 sm:pt-6 sm:pb-5 sm:px-4 lg:px-6 border-t border-gray-100/50 dark:border-white/5">
            <div className="max-w-4xl mx-auto">
              <SuggestedPrompts />
              {chatInput}
            </div>
          </div>
        </>
      ) : (
        <EmptyState>{chatInput}</EmptyState>
      )}
    </div>
  );
}
